import { ReactNode, useEffect, useMemo } from "react";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { deleteAllCardsModalStore } from "features/DeleteAllCardsModal";
import { Filter, useFilterContext } from "features/Filter";
import { filteredCardsStore } from "features/Filter";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel/hooks/Panel.hooks";
import { DeleteAllButton } from "features/SeaCardActions";
import { Search } from "features/Search";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { StatusFilter } from "features/StatusFilter";
import { Tags } from "features/Tags";
import { updateAllCardsModalStore } from "features/UpdateAllCardsModal";
// import { UploadButton } from "features/UploadFileModal";
import { AIR_QUERY_CACHE_KEY, airSavedCardStore, useAirSavedCardsInfiniteQuery } from "entities/AirSavedCard";
import { airSearchCardStore } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { BackButton } from "entities/BackButton/ui";
import { PanelViewsList } from "entities/Panel";
import { parcelSearchCardStore } from "entities/ParcelSearchCard";
import { railSearchCardStore } from "entities/RailSearchCard";
import { roadSearchCardStore } from "entities/RoadSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { SEA_QUERY_CACHE_KEY, seaSavedCardStore, useSeaSavedCardsInfiniteQuery } from "entities/SeaSavedCard";
import { seaSearchCardStore } from "entities/SeaSearchCard";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { Tooltip } from "shared/ui/Tooltip";
import { UpdateAllButton } from "shared/ui/UpdateAllButton";
import { CardsByTagView, FilteredCardsView, IntroView, MainView } from "./PanelViews";
import { OpenedCardView } from "./PanelViews/OpenedCard/OpenedCardView";
import styles from "./Panel.module.scss";

const panelViewsMap: Record<PanelViewsList, ReactNode> = {
  [PanelViewsList.intro]: <IntroView />,
  [PanelViewsList.main]: <MainView />,
  [PanelViewsList.openedCard]: <OpenedCardView />,
  [PanelViewsList.cardsByTag]: <CardsByTagView />,
  [PanelViewsList.filteredCards]: <FilteredCardsView />,
};

export const Panel: RFC<{
  className?: string;
}> = ({ className }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const {
    currentView,
    isCurrentViewMain,
    isCurrentViewIntro,
    showPreviousView,
    showIntroView,
    showMainView,
    clearViewHistory,
    isCurrentViewOpenedCard,
    isCurrentViewCardsByTag,
  } = usePanelViews();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const seaSavedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const seaSearchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const airSavedCards = useAppSelector(airSavedCardStore.selectors.savedCards);
  const airSearchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const railSearchCards = useAppSelector(railSearchCardStore.selectors.searchCards);
  const roadSearchCards = useAppSelector(roadSearchCardStore.selectors.searchCards);
  const parcelSearchCards = useAppSelector(parcelSearchCardStore.selectors.searchCards);
  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);
  const isCardSearchVisible = useAppSelector(appStore.selectors.isCardSearchVisible);

  const {
    setFilter,
    innerFilterState,
    resetFilter,
    isAirFilter,
    isSeaFilter,
    isOpenFilter,
    statusFilter,
    setStatusFilter,
    allStatuses,
    setAllStatuses,
    setOpenFilter,
  } = useFilterContext();

  // Needs to hide back button when only status filter is shown
  const isOnlyStatusFilterShown = useMemo(() => {
    const isOrderQueryEmpty = innerFilterState.filter.order.length === 0;
    const statusFilter = innerFilterState.filter.where.status;
    const isWhereQueryOnlyHasStatus = Object.keys(innerFilterState.filter.where).length === 1 && !!statusFilter;
    return isOrderQueryEmpty && isWhereQueryOnlyHasStatus;
  }, [innerFilterState.filter]);

  const isShipmentTypeConfirmed = useAppSelector(searchFilterStore.selectors.isShipmentTypeConfirmed);
  const isSeaShipment = isShipmentTypeConfirmed && ShipmentTypeChecker.isSea(shipment);
  const isAirShipment = isShipmentTypeConfirmed && ShipmentTypeChecker.isAir(shipment);

  const { resetSeaCardsQuery } = useSeaSavedCardsInfiniteQuery({
    // Fetch here so saved cards load before MainView/SeaView mounts (intro would block SeaView otherwise).
    shouldRequestOnMount: isAuthenticated && isSeaShipment,
    onSuccess: ({ page }) => {
      dispatch(seaSavedCardStore.actions.appendSavedCardsAction({ data: page }));
    },
    onSettled: ({ page }) => {
      if (!page?.length && !seaSearchCards.length) {
        showIntroView();
      }
    },
    queryKey: [SEA_QUERY_CACHE_KEY],
  });

  const { resetAirCardsQuery } = useAirSavedCardsInfiniteQuery({
    // As for sea: fetch here, before MainView/AirView mounts, or the intro
    // view wins the race and the saved cards are never asked for.
    shouldRequestOnMount: isAuthenticated && isAirShipment,
    onSuccess: ({ page }) => {
      dispatch(airSavedCardStore.actions.appendSavedCardsAction({ data: page }));
    },
    onSettled: ({ page }) => {
      if (!page?.length && !airSearchCards.length) {
        showIntroView();
      }
    },
    queryKey: [AIR_QUERY_CACHE_KEY],
  });

  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  const shouldRenderFilter =
    isAuthenticated &&
    (!!seaSavedCards.length || !!airSavedCards.length) &&
    !isCurrentViewOpenedCard &&
    (isSeaShipment || isAirShipment);

  const shouldRenderBackButton =
    !isCurrentViewMain &&
    !isCurrentViewIntro &&
    isCardSearchVisible &&
    (!isOnlyStatusFilterShown || isCurrentViewOpenedCard);

  const shouldRenderSearch = !isCurrentViewOpenedCard && isCardSearchVisible;
  const isHaveSaivedCard = !!seaSavedCards.length || !!airSavedCards.length;

  const shouldRenderStatusFilter =
    !isCurrentViewOpenedCard &&
    isAuthenticated &&
    isHaveSaivedCard &&
    !ShipmentTypeChecker.isRail(shipment) &&
    !ShipmentTypeChecker.isRoad(shipment) &&
    !ShipmentTypeChecker.isParcel(shipment);
  const shouldRenderToolsBar = shouldRenderStatusFilter;
  // const shouldRenderUploadFileBtn = isCardSearchVisible && isAuthenticated && ShipmentTypeChecker.isSea(shipment);
  const shouldRenderDeleteAllButton = isAuthenticated;
  const shouldRenderUpdateAllButton = isAuthenticated;
  const isTagsVisible = isAuthenticated && (isCurrentViewMain || isCurrentViewCardsByTag);

  const hasCards = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaSavedCards.length || seaSearchCards.length;
      case ShipmentType.AIR:
        return airSavedCards.length || airSearchCards.length;
      case ShipmentType.RAIL:
        return railSearchCards.length;
      case ShipmentType.ROAD:
        return roadSearchCards.length;
      case ShipmentType.PARCEL:
        return parcelSearchCards.length;
      default:
        return false;
    }
  }, [
    shipment,
    seaSavedCards,
    seaSearchCards,
    airSavedCards,
    airSearchCards,
    railSearchCards,
    roadSearchCards,
    parcelSearchCards,
  ]);

  useEffect(() => {
    if (isCurrentViewMain && !hasCards) {
      showIntroView();
    }
    if (isCurrentViewIntro && hasCards) {
      if (openedCard) clearViewHistory();
      showMainView();
    }
    if (isCurrentViewOpenedCard && requestedFromUrl) {
      clearViewHistory();
    }
  }, [currentView, hasCards]);

  useEffect(() => {
    if (!isShipmentTypeConfirmed) return;
    if (ShipmentTypeChecker.isSea(shipment)) {
      dispatch(airSavedCardStore.actions.setSavedCardsAction([]));
      resetSeaCardsQuery();
    }
    if (ShipmentTypeChecker.isAir(shipment)) {
      dispatch(seaSavedCardStore.actions.setSavedCardsAction([]));
      resetAirCardsQuery();
    }
    if (isOpenFilter) {
      setOpenFilter(false);
    }
  }, [shipment, isShipmentTypeConfirmed]);

  return (
    <div className={cn(styles.panel, className)}>
      {shouldRenderBackButton && (
        <div className={cn(styles.elementsWrapper, styles.backButtonWrapper)}>
          <div className={styles.toolsWrapper}>
            <BackButton showPreviousView={showPreviousView} />
          </div>
        </div>
      )}

      {shouldRenderSearch && (
        <div className={styles.elementsWrapper}>
          <Search openedCardReference={openedCard?.reference ?? null} openCard={openCard} />
        </div>
      )}

      {shouldRenderStatusFilter && (
        <div className={styles.statusFilterWrapper}>
          <StatusFilter
            setFilter={setFilter}
            innerFilterState={innerFilterState}
            resetFilter={resetFilter}
            setFilteredCardAction={(cards) => ({
              type: filteredCardsStore.actions.setFilteredCardsAction.type,
              payload: cards,
            })}
            isAirFilter={isAirFilter}
            isSeaFilter={isSeaFilter}
            filter={innerFilterState.filter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            allStatuses={allStatuses}
            setAllStatuses={setAllStatuses}
          />
        </div>
      )}

      {shouldRenderToolsBar && (
        <div className={cn(styles.toolsContentWrapper, styles.elementsWrapper)}>
          <div className={styles.toolsContent}>
            {isTagsVisible && <Tags />}
            <ul className={styles.buttonsWrapper}>
              {shouldRenderUpdateAllButton && (
                <li>
                  <Tooltip
                    triggerElement={
                      <UpdateAllButton
                        onClick={() => {
                          dispatch(updateAllCardsModalStore.actions.openUpdateAllCardsModalAction());
                        }}
                      />
                    }
                    offsetY={15}
                  >
                    <span className={styles.updateAllButtonTooltipText}>
                      {t("This action will update all listed shipments at once")}
                    </span>
                  </Tooltip>
                </li>
              )}
              {shouldRenderDeleteAllButton && (
                <li>
                  <Tooltip
                    triggerElement={
                      <DeleteAllButton
                        onClick={() => {
                          dispatch(deleteAllCardsModalStore.actions.openDeleteAllCardsModalAction());
                        }}
                      />
                    }
                    offsetY={15}
                  >
                    <span className={styles.deleteAllButtonTooltipText}>
                      {t("This action will permanently delete all listed shipments")}
                    </span>
                  </Tooltip>
                </li>
              )}
              {/* {shouldRenderUploadFileBtn && (
                <li>
                  <Tooltip triggerElement={<UploadButton />} className={styles.uploadFileOpener} offsetY={15}>
                    <span className={styles.uploadFileOpenerTooltipText}>{t("Upload excel file")}</span>
                  </Tooltip>
                </li>
              )} */}
              {shouldRenderFilter && (
                <li>
                  <Filter filterContentClassName={cn({ [styles.isBackButtonVisible]: shouldRenderBackButton })} />
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {!isOpenFilter && <div className={cn(styles.panelViews)}>{panelViewsMap[currentView]}</div>}
    </div>
  );
};
